import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, EmptyState, Skeleton } from '../ui';
import AssignedPathCard from '../learn/AssignedPathCard';
import { useAuth } from '../../context/AuthContext';
import { useLearning } from '../../context/LearningContext';

/**
 * Assigned learning paths, moved from the retired `/learn` dashboard into the
 * Mission Center (spec §8.1 — `/learn` is now only a redirect).
 *
 * `AssignedPathCard` is reused untouched: it is a working, shipped feature and
 * this package is its new home, not its replacement. Its own copy is still
 * hardcoded English and it paints two thresholds with raw hex — both are
 * reported as follow-up debt rather than fixed here, because rewriting it
 * would mean rewriting a component this package does not own.
 */
export function MissionPaths() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { startSession, startDialogue } = useLearning();

  const headingId = useId();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/learning/paths', {
          signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setPaths(Array.isArray(data.paths) ? data.paths : Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const openRecall = useCallback(
    async (path) => {
      try {
        const session = await startSession(null, { pathId: path.id });
        if (session?.sessionId) navigate(`/missions/session/${session.sessionId}`);
      } catch (err) {
        setError(err);
      }
    },
    [navigate, startSession]
  );

  const openDialogue = useCallback(
    async (path) => {
      try {
        const dialogue = await startDialogue(path.id);
        if (dialogue?.dialogueId) navigate(`/missions/dialogue/${dialogue.dialogueId}`);
      } catch (err) {
        setError(err);
      }
    },
    [navigate, startDialogue]
  );

  return (
    <section className="etch-mission-section" aria-labelledby={headingId}>
      <div className="etch-mission-section__head">
        <h2 id={headingId} className="etch-mission-section__title">
          {t('missions.pathsTitle')}
        </h2>
      </div>
      <p className="etch-mission-section__description">{t('missions.pathsDescription')}</p>

      {loading && (
        <div className="etch-stack">
          <p className="etch-visually-hidden" role="status">
            {t('states.loading')}
          </p>
          <Skeleton height="9rem" />
        </div>
      )}

      {error && !loading && (
        <Alert tone="danger" title={t('states.error')}>
          <p>{t('missions.pathsFailedBody')}</p>
          <Button variant="secondary" size="compact" onClick={() => load()}>
            {t('states.retry')}
          </Button>
        </Alert>
      )}

      {!loading && !error && paths.length === 0 && (
        <EmptyState title={t('missions.pathsEmptyTitle')}>{t('missions.pathsEmptyBody')}</EmptyState>
      )}

      {!loading && !error && paths.length > 0 && (
        <ul className="etch-mission-section__list">
          {paths.map((path) => (
            <li key={path.id}>
              <AssignedPathCard
                path={path}
                onDeepDive={() => openDialogue(path)}
                onRecall={() => openRecall(path)}
                onSimulate={() => {}}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default MissionPaths;
